class DlProductGalleryM extends HTMLElement {
  constructor() {
    super();
    this.galleries = Array.from(this.querySelectorAll('.dl-product-gallery-container-m'));
    this.pointerStartX = null;
    this.wasMobileM = window.innerWidth <= 990;
    this.onResize = () => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        const gallery = this.visibleGallery();
        const isMobile = window.innerWidth <= 990;
        if (isMobile !== this.wasMobileM) {
          const shell = gallery?.querySelector('.dl-product-gallery-shell-m');
          this.goTo(shell, Number(shell?.dataset.galleryIndexM || 0), false);
          this.wasMobileM = isMobile;
        }
      });
    };
    window.addEventListener('resize', this.onResize, { passive: true });
    this.bindEvents();
    this.mount(this.visibleGallery());
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    cancelAnimationFrame(this.resizeFrame);
    this.querySelectorAll('.dl-product-gallery-video-m video').forEach((video) => {
      this.cancelVideoRetry(video);
      if (video._dlWarmupIdleM && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(video._dlWarmupIdleM);
      }
      if (video._dlWarmupTimerM) window.clearTimeout(video._dlWarmupTimerM);
    });
  }

  static toggle(variantId) {
    document.querySelectorAll('dl-product-gallery-m').forEach((gallery) => gallery.showVariant(variantId));
  }

  visibleGallery() {
    return this.galleries.find((gallery) => !gallery.classList.contains('dl-hide-m'));
  }

  bindEvents() {
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (!event.target.closest('.dl-product-gallery-viewport-m')) return;
      this.pointerStartX = event.clientX;
    });
    this.addEventListener('pointerup', (event) => {
      if (this.pointerStartX === null) return;
      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      if (Math.abs(distance) < 42) return;
      const gallery = this.visibleGallery();
      if (!gallery) return;
      const shell = gallery.querySelector('.dl-product-gallery-shell-m');
      this.suppressSlideClick = true;
      setTimeout(() => { this.suppressSlideClick = false; }, 0);
      this.moveBy(shell, distance < 0 ? 1 : -1);
    });
    this.addEventListener('pointercancel', () => { this.pointerStartX = null; });
  }

  mount(gallery) {
    if (!gallery) return;
    const template = gallery.querySelector('template[data-gallery-template-m]');
    if (template) {
      gallery.appendChild(template.content.cloneNode(true));
      template.remove();
    }
    const shell = gallery.querySelector('.dl-product-gallery-shell-m');
    if (!shell || shell.dataset.mountedM) return;
    shell.dataset.mountedM = 'true';
    shell.dataset.galleryIndexM = '0';
    shell.dataset.lastPhotoIndexM = '0';
    const firstVideo = shell.querySelector('.dl-product-gallery-slide-m[data-media-type="video"]');
    shell.dataset.lastVideoIndexM = firstVideo?.dataset.galleryIndexM || '';
    shell.querySelectorAll('.dl-product-gallery-video-m video').forEach((video) => {
      const slide = video.closest('.dl-product-gallery-video-m');
      video.addEventListener('playing', () => slide?.classList.add('dl-product-gallery-video-ready-m'));
      video.addEventListener('loadstart', () => slide?.classList.remove('dl-product-gallery-video-ready-m'));
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.preload = 'metadata';
      this.scheduleVideoWarmup(video);
    });
    this.setMode(shell, 'photo', false);
  }

  scheduleVideoWarmup(video) {
    const loadVideo = () => {
      video._dlWarmupIdleM = null;
      video._dlWarmupTimerM = null;
      if (!video.isConnected || navigator.connection?.saveData || video.readyState >= 3) return;
      video.preload = 'auto';
      video.load();
    };
    if ('requestIdleCallback' in window) {
      video._dlWarmupIdleM = window.requestIdleCallback(loadVideo, { timeout: 1500 });
    } else {
      video._dlWarmupTimerM = window.setTimeout(loadVideo, 600);
    }
  }

  cancelVideoRetry(video) {
    if (!video?._dlCanPlayM) return;
    video.removeEventListener('canplay', video._dlCanPlayM);
    video._dlCanPlayM = null;
  }

  playInlineVideo(video, slide, shell) {
    this.cancelVideoRetry(video);
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;
    video.preload = 'auto';
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    const shouldPlay = () => (
      !slide.hidden
      && shell.dataset.activeGalleryModeM === 'video'
      && video.closest('.dl-product-gallery-slide-m') === slide
    );
    const attemptPlay = () => {
      if (!shouldPlay()) return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };
    const retry = () => {
      video._dlCanPlayM = null;
      attemptPlay();
    };
    if (video.readyState < 3) {
      video._dlCanPlayM = retry;
      video.addEventListener('canplay', retry, { once: true });
    }
    if (video.readyState === 0) video.load();
    attemptPlay();
  }

  showVariant(variantId) {
    const targetId = String(variantId);
    let targetGallery = null;
    this.galleries.forEach((gallery) => {
      const matches = (gallery.dataset.dlVariantIds || '').split(',').includes(targetId);
      gallery.classList.toggle('dl-hide-m', !matches);
      if (matches) targetGallery = gallery;
    });
    if (!targetGallery) return;
    this.mount(targetGallery);
    const shell = targetGallery.querySelector('.dl-product-gallery-shell-m');
    shell.querySelectorAll('.dl-product-gallery-featured-image-m').forEach((image) => {
      image.dataset.active = String(image.dataset.dlVariantId === targetId);
    });
    const activeImage = shell.querySelector(`.dl-product-gallery-featured-image-m[data-dl-variant-id="${targetId}"]`);
    const thumbnailImage = shell.querySelector('.dl-product-gallery-thumbnail-m[data-gallery-index-m="0"] img');
    if (activeImage && thumbnailImage) {
      thumbnailImage.src = activeImage.currentSrc || activeImage.src;
      thumbnailImage.srcset = '';
    }
    this.goTo(shell, 0, false);
    this.dataset.currentVariantId = targetId;
  }

  onClick(event) {
    const arrowButton = event.target.closest('.dl-product-gallery-arrow-m[data-gallery-direction-m]');
    if (arrowButton) {
      const shell = arrowButton.closest('.dl-product-gallery-shell-m');
      this.moveBy(shell, Number(arrowButton.dataset.galleryDirectionM));
      return;
    }

    const modeButton = event.target.closest('.dl-product-gallery-mode-m[data-gallery-mode-m]');
    if (modeButton) {
      this.changeMode(modeButton);
      return;
    }

    const indexButton = event.target.closest('[data-gallery-index-m]');
    if (indexButton?.classList.contains('dl-product-gallery-thumbnail-m')) {
      const shell = indexButton.closest('.dl-product-gallery-shell-m');
      this.goTo(shell, Number(indexButton.dataset.galleryIndexM));
      return;
    }

    const slide = event.target.closest('.dl-product-gallery-slide-m');
    if (!slide) return;
    if (this.suppressSlideClick) {
      this.suppressSlideClick = false;
      return;
    }
    if (window.innerWidth > 990) {
      this.collectItemEvent('pdp_gallery_item_click', slide);
    }
    this.openFullGallery(slide);
  }

  async changeMode(modeButton) {
    const shell = modeButton.closest('.dl-product-gallery-shell-m');
    if (modeButton.dataset.galleryModeM === 'photo') {
      this.setMode(shell, 'photo');
      return;
    }
    if (modeButton.dataset.galleryModeM === 'video') {
      this.setMode(shell, 'video');
      return;
    }
    const arLink = modeButton.dataset.arLinkM;
    await this.collectButtonEvent('view_in_ar', modeButton.dataset.productHandle);
    if (window.innerWidth <= 990 && arLink) {
      window.location.href = arLink;
      return;
    }
    const arPopup = shell.querySelector('dl-product-gallery-ar-m');
    if (arPopup && typeof arPopup.show === 'function') {
      arPopup.show();
      return;
    }
    if (arLink) window.location.href = arLink;
  }

  async collectButtonEvent(name, productHandle) {
    if (!productHandle || typeof DLPixelDataHelper === 'undefined' || typeof DLPixelH === 'undefined') return;
    try {
      const productData = await DLPixelDataHelper.ProductHelper.getProductByHandle(productHandle);
      DLPixelH.capture('pdp_gallery_btn_click', { name, ...productData });
    } catch (error) {
      console.error('AR analytics error:', error);
    }
  }

  async collectItemEvent(eventName, slide) {
    const shell = slide?.closest('.dl-product-gallery-shell-m');
    const productHandle = shell?.dataset.productHandle;
    if (!shell || !productHandle || typeof DLPixelDataHelper === 'undefined' || typeof DLPixelH === 'undefined') return;

    const activeImage = slide.querySelector('.dl-product-gallery-featured-image-m[data-active="true"]');
    const media = activeImage || slide.querySelector('img, source');
    const mediaType = slide.classList.contains('dl-product-gallery-featured-m')
      ? 'featured-image'
      : slide.dataset.mediaType === 'video' ? 'video' : 'image';
    let imageIndex = Number(slide.dataset.galleryIndexM || 0) + 1;
    if (window.innerWidth > 990 && mediaType === 'image' && !shell.dataset.lastVideoIndexM) {
      imageIndex += 1;
    }
    const mediaSrc = media?.currentSrc || media?.src || '';

    try {
      const productData = await DLPixelDataHelper.ProductHelper.getProductByHandle(productHandle);
      DLPixelH.capture(eventName, {
        ...productData,
        galleryType: 'glide',
        source: eventName === 'pdp_gallery_item_view' ? 'mobile' : undefined,
        imageIndex,
        mediaType,
        mediaSrc
      });
    } catch (error) {
      console.error('Gallery item analytics error:', error);
    }
  }

  setMode(shell, mode, animate = true) {
    if (!shell) return;
    const indexKey = mode === 'video' ? 'lastVideoIndexM' : 'lastPhotoIndexM';
    const fallbackSlide = shell.querySelector(`.dl-product-gallery-slide-m[data-media-type="${mode}"]`);
    if (!fallbackSlide) return;
    const targetIndex = Number(shell.dataset[indexKey] || fallbackSlide.dataset.galleryIndexM || 0);
    this.goTo(shell, targetIndex, animate);
  }

  moveBy(shell, direction) {
    if (!shell) return;
    const mode = shell.dataset.activeGalleryModeM || 'photo';
    const visibleSlides = Array.from(shell.querySelectorAll(`.dl-product-gallery-slide-m[data-media-type="${mode}"]`));
    const currentIndex = Number(shell.dataset.galleryIndexM || 0);
    const currentPosition = Math.max(0, visibleSlides.findIndex((slide) => Number(slide.dataset.galleryIndexM) === currentIndex));
    const nextPosition = Math.min(Math.max(currentPosition + direction, 0), visibleSlides.length - 1);
    this.goTo(shell, Number(visibleSlides[nextPosition].dataset.galleryIndexM));
  }

  goTo(shell, requestedIndex, animate = true) {
    if (!shell) return;
    const slides = Array.from(shell.querySelectorAll('.dl-product-gallery-slide-m'));
    if (!slides.length) return;
    const targetSlide = slides.find((slide) => Number(slide.dataset.galleryIndexM) === requestedIndex) || slides[0];
    const mode = targetSlide.dataset.mediaType || 'photo';
    shell.dataset.activeGalleryModeM = mode;
    slides.forEach((slide) => { slide.hidden = slide.dataset.mediaType !== mode; });
    const visibleSlides = slides.filter((slide) => slide.dataset.mediaType === mode);
    const position = Math.max(0, visibleSlides.indexOf(targetSlide));
    const index = Number(targetSlide.dataset.galleryIndexM || 0);
    const track = shell.querySelector('.dl-product-gallery-track-m');
    if (!animate) track.style.transition = 'none';
    shell.dataset.galleryIndexM = String(index);
    const currentCounter = shell.querySelector('[data-gallery-current-m]');
    const totalCounter = shell.querySelector('[data-gallery-total-m]');
    if (currentCounter) currentCounter.textContent = String(position + 1);
    if (totalCounter) totalCounter.textContent = String(visibleSlides.length);
    const thumbnails = shell.querySelector('.dl-product-gallery-thumbnails-m');
    const keepSingleVideoThumbnail = window.innerWidth <= 990 && mode === 'video';
    if (thumbnails) thumbnails.hidden = visibleSlides.length <= 1 && !keepSingleVideoThumbnail;
    const arrows = shell.querySelector('.dl-product-gallery-arrows-m');
    if (arrows) arrows.hidden = visibleSlides.length <= 1;
    const previousArrow = shell.querySelector('.dl-product-gallery-arrow-previous-m');
    const nextArrow = shell.querySelector('.dl-product-gallery-arrow-next-m');
    if (previousArrow) previousArrow.disabled = position === 0;
    if (nextArrow) nextArrow.disabled = position === visibleSlides.length - 1;
    track.style.transform = `translate3d(${-position * 100}%, 0, 0)`;
    if (!animate) requestAnimationFrame(() => { track.style.transition = ''; });

    const activeFeaturedImage = targetSlide.querySelector('.dl-product-gallery-featured-image-m[data-active="true"]');
    const viewKey = `${index}:${activeFeaturedImage?.dataset.dlVariantId || ''}`;
    if (window.innerWidth <= 990 && shell.dataset.lastTrackedViewM !== viewKey) {
      shell.dataset.lastTrackedViewM = viewKey;
      this.collectItemEvent('pdp_gallery_item_view', targetSlide);
    }

    shell.querySelectorAll('.dl-product-gallery-thumbnail-m').forEach((button) => {
      button.hidden = button.dataset.mediaType !== mode;
      const selected = Number(button.dataset.galleryIndexM) === index;
      button.classList.toggle('dl-selected-m', selected);
      button.setAttribute('aria-current', String(selected));
    });

    const isVideo = mode === 'video';
    shell.dataset[isVideo ? 'lastVideoIndexM' : 'lastPhotoIndexM'] = String(index);
    shell.querySelectorAll('.dl-product-gallery-mode-m[data-gallery-mode-m]').forEach((button) => {
      const selected = button.dataset.galleryModeM === (isVideo ? 'video' : 'photo');
      button.classList.toggle('dl-selected-m', selected);
    });
    shell.querySelectorAll('video').forEach((video) => {
      const isSelectedVideo = video.closest('.dl-product-gallery-slide-m') === targetSlide;
      const autoplayMobile = shell.dataset.enableAutoplayMobileM !== 'false';
      if (isSelectedVideo && (window.innerWidth > 990 || autoplayMobile)) {
        this.playInlineVideo(video, targetSlide, shell);
      } else {
        this.cancelVideoRetry(video);
        video.pause();
      }
    });
    const thumbnail = shell.querySelector(`.dl-product-gallery-thumbnail-m[data-gallery-index-m="${index}"]`);
    const thumbnailScroller = thumbnail?.parentElement;
    if (thumbnail && thumbnailScroller) {
      thumbnailScroller.scrollLeft = thumbnail.offsetLeft - (thumbnailScroller.clientWidth - thumbnail.offsetWidth) / 2;
    }
  }

  openFullGallery(slide) {
    const gallery = slide.closest('.dl-product-gallery-container-m');
    const isMobile = window.innerWidth <= 990;
    if (isMobile && slide.dataset.mediaType === 'video') {
      const videoPopup = gallery?.querySelector('dl-product-video-pop-x-mobile');
      if (videoPopup && typeof videoPopup.show === 'function') videoPopup.show();
      return;
    }
    const popup = gallery?.querySelector('dl-product-gallery-pop-x-desktop');
    if (!popup || typeof popup.show !== 'function') return;
    let targetIndex = Number(slide.dataset.popupIndexM || 0);
    if (isMobile && targetIndex > 1 && gallery.querySelector('.dl-product-gallery-slide-m[data-media-type="video"]')) {
      targetIndex -= 1;
    }
    popup.targetSlideIndex = targetIndex;
    popup.show();
  }
}

if (!customElements.get('dl-product-gallery-m')) {
  customElements.define('dl-product-gallery-m', DlProductGalleryM);
}

window.DlProductGalleryM = DlProductGalleryM;

class DlProductGalleryArM extends DlPopBaseH {
  connectedCallback() {
    if (this._initializedM) return;
    super.connectedCallback();
    this._initializedM = true;
  }

  show() {
    if (!this._originalParentM) {
      this._originalParentM = this.parentNode;
      this._originalNextSiblingM = this.nextSibling;
    }
    this.closest('dl-product-h')?.appendChild(this);
    super.show();
    document.documentElement.classList.add('dl-product-gallery-ar-open-m');
    document.body.classList.add('dl-product-gallery-ar-open-m');
  }

  hide() {
    super.hide();
    document.documentElement.classList.remove('dl-product-gallery-ar-open-m');
    document.body.classList.remove('dl-product-gallery-ar-open-m');
    if (!this._originalParentM) return;
    if (this._originalNextSiblingM?.isConnected) {
      this._originalParentM.insertBefore(this, this._originalNextSiblingM);
    } else {
      this._originalParentM.appendChild(this);
    }
    this._originalParentM = null;
    this._originalNextSiblingM = null;
  }
}

if (!customElements.get('dl-product-gallery-ar-m')) {
  customElements.define('dl-product-gallery-ar-m', DlProductGalleryArM);
}

class DlProductGalleryPopM extends DlPopBaseH {
  connectedCallback() {
    if (this._initializedM) return;
    super.connectedCallback();
    this._initializedM = true;
    this.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('.dl-thumbnail-item');
      if (!thumbnail || !this.contains(thumbnail)) return;
      event.preventDefault();
      event.stopPropagation();
      this.goTo(Number(thumbnail.dataset.popupIndexM || 0));
    }, true);
  }

  hydrateContent() {
    if (this._hydratedM) return;
    const template = this.querySelector('template[data-dl-popup-template]');
    const content = this.querySelector('[data-dl-popup-content]');
    if (!template || !content) return;
    content.appendChild(template.content.cloneNode(true));
    template.remove();
    this._hydratedM = true;
  }

  show() {
    clearTimeout(this.restoreTimerM);
    this.hydrateContent();
    this.prepareResponsiveContent();
    this.updateFeaturedImage();
    if (!this._originalParentM) {
      this._originalParentM = this.parentNode;
      this._originalNextSiblingM = this.nextSibling;
    }
    this.closest('dl-product-h')?.appendChild(this);
    super.show();
    requestAnimationFrame(() => this.initializeGlide());
  }

  prepareResponsiveContent() {
    const isMobile = window.innerWidth <= 990;
    if (isMobile === this._mobileContentM) return;
    this._mobileContentM = isMobile;
    if (isMobile) {
      this._detachedVideoContentM = Array.from(this.querySelectorAll(
        '.dl-product-gallery-pop-x-media-item-video, .dl-thumbnail-video-item-m'
      )).map((node) => {
        const placeholder = document.createComment('mobile-gallery-video');
        node.replaceWith(placeholder);
        return { node, placeholder };
      });
      return;
    }
    (this._detachedVideoContentM || []).forEach(({ node, placeholder }) => {
      if (placeholder.isConnected) placeholder.replaceWith(node);
    });
    this._detachedVideoContentM = [];
  }

  hide() {
    this.pauseVideos();
    this.mainGlideM?.destroy();
    this.mainGlideM = null;
    super.hide();
    this.restoreTimerM = setTimeout(() => this.restorePosition(), 300);
  }

  restorePosition() {
    if (!this._originalParentM) return;
    if (this._originalNextSiblingM?.isConnected) {
      this._originalParentM.insertBefore(this, this._originalNextSiblingM);
    } else {
      this._originalParentM.appendChild(this);
    }
    this._originalParentM = null;
    this._originalNextSiblingM = null;
  }

  updateFeaturedImage() {
    const gallery = this.closest('.dl-product-gallery-container-m');
    const variantId = gallery?.closest('dl-product-gallery-m')?.dataset.currentVariantId;
    if (!variantId) return;
    let activeImage = null;
    this.querySelectorAll('.dl-variant-featured-item').forEach((image) => {
      const isActive = image.dataset.dlVariantId === variantId;
      image.classList.toggle('dl-show', isActive);
      if (isActive) activeImage = image;
    });
    const featuredThumbnail = this.querySelector('.dl-thumbnail-first-item .dl-thumbnail-img');
    if (!activeImage || !featuredThumbnail) return;
    featuredThumbnail.src = activeImage.currentSrc || activeImage.src;
    featuredThumbnail.srcset = '';
  }

  initializeGlide() {
    const main = this.querySelector('.dl-product-gallery-pop-x-desktop-main');
    if (!main || typeof Glide === 'undefined') return;
    this.mainGlideM?.destroy();
    const startAt = Math.max(0, Number(this.targetSlideIndex) || 0);
    this.mainGlideM = new Glide(main, {
      type: 'carousel',
      perView: 1,
      gap: 0,
      startAt,
      autoplay: false,
      keyboard: true,
      waitForTransition: false,
      dragThreshold: 120,
      swipeThreshold: 120
    });
    this.mainGlideM.on('mount.after', () => this.syncState());
    this.mainGlideM.on('run.after', () => this.syncState());
    this.mainGlideM.mount();

    const thumbnailMain = this.querySelector('.dl-product-gallery-pop-x-desktop-thumbnails-main');
    if (thumbnailMain) {
      const thumbnailTrack = thumbnailMain.querySelector('.glide__slides');
      Array.from(thumbnailTrack?.children || []).forEach((thumbnail, index) => {
        if (thumbnail.classList.contains('dl-thumbnail-item')) {
          thumbnail.dataset.popupIndexM = String(index);
        }
      });
    }
    this.goTo(startAt);
  }

  goTo(index) {
    if (!this.mainGlideM) return;
    const slideCount = this.querySelectorAll('.dl-product-gallery-pop-x-desktop-main .glide__slide:not(.glide__slide--clone)').length;
    const safeIndex = Math.min(Math.max(index, 0), Math.max(0, slideCount - 1));
    this.mainGlideM.go(`=${safeIndex}`);
    this.syncState(safeIndex);
  }

  syncState(forcedIndex) {
    const index = Number.isFinite(forcedIndex) ? forcedIndex : Number(this.mainGlideM?.index || 0);
    let selectedThumbnail = null;
    this.querySelectorAll('.dl-thumbnail-item').forEach((thumbnail) => {
      thumbnail.classList.toggle('dl-selected', Number(thumbnail.dataset.popupIndexM) === index);
      if (Number(thumbnail.dataset.popupIndexM) === index) selectedThumbnail = thumbnail;
    });
    const thumbnailViewport = this.querySelector('.dl-product-gallery-pop-x-desktop-thumbnails .glide__track');
    if (selectedThumbnail && thumbnailViewport) {
      thumbnailViewport.scrollTo({
        behavior: 'smooth',
        left: selectedThumbnail.offsetLeft - (thumbnailViewport.clientWidth - selectedThumbnail.offsetWidth) / 2
      });
    }
    this.pauseVideos();
    const slides = this.querySelectorAll('.dl-product-gallery-pop-x-desktop-main .glide__slide:not(.glide__slide--clone)');
    const video = slides[index]?.querySelector('video:not(.hidden), video');
    if (video) {
      video.muted = true;
      video.playsInline = true;
      video.play().catch(() => {});
    }
  }

  pauseVideos() {
    this.querySelectorAll('.dl-product-gallery-pop-x-media-item-video video').forEach((video) => video.pause());
  }
}

if (!customElements.get('dl-product-gallery-pop-x-desktop')) {
  customElements.define('dl-product-gallery-pop-x-desktop', DlProductGalleryPopM);
}

class DlProductVideoPopM extends DlPopBaseH {
  connectedCallback() {
    if (this._initializedM) return;
    super.connectedCallback();
    this._initializedM = true;
    this.videoM = this.querySelector('video');
    this.videoM?.removeAttribute('autoplay');
    this.videoM?.pause();
  }

  show() {
    if (!this._originalParentM) {
      this._originalParentM = this.parentNode;
      this._originalNextSiblingM = this.nextSibling;
    }
    this.closest('dl-product-h')?.appendChild(this);
    super.show();
    if (!this.videoM) return;
    try {
      this.videoM.currentTime = 0;
    } catch (error) {
      // The media metadata may not be ready on the first open.
    }
    const playPromise = this.videoM.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  hide() {
    this.videoM?.pause();
    if (this.videoM) {
      try {
        this.videoM.currentTime = 0;
      } catch (error) {
        // Ignore browsers that cannot seek before metadata is available.
      }
    }
    super.hide();
    setTimeout(() => this.restorePosition(), 300);
  }

  restorePosition() {
    if (!this._originalParentM) return;
    if (this._originalNextSiblingM?.isConnected) {
      this._originalParentM.insertBefore(this, this._originalNextSiblingM);
    } else {
      this._originalParentM.appendChild(this);
    }
    this._originalParentM = null;
    this._originalNextSiblingM = null;
  }
}

if (!customElements.get('dl-product-video-pop-x-mobile')) {
  customElements.define('dl-product-video-pop-x-mobile', DlProductVideoPopM);
}
